
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authService } from '@/services/auth';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

export default function ResetPasswordForm() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await authService.updatePassword(password);
      
      if (error) {
        setError(error.message);
      } else {
        setSuccess(true);
        
        // Sign out the user after successful password update
        setTimeout(async () => {
          try {
            await supabase.auth.signOut();
            // Navigate to login page
            navigate('/auth/login', { 
              replace: true,
              state: { passwordResetSuccess: true }
            });
          } catch (signOutError) {
            console.error('Error signing out:', signOutError);
            // Still navigate to login even if signout fails
            navigate('/auth/login', { 
              replace: true,
              state: { passwordResetSuccess: true }
            });
          }
        }, 2000);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show success message after password update
  if (success) {
    return (
      <div className="space-y-4">
        <Alert>
          <AlertDescription>
            Password updated successfully! You will be redirected to the login page to sign in with your new password.
          </AlertDescription>
        </Alert>
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="space-y-2">
        <Label htmlFor="password">New Password</Label>
        <Input
          id="password"
          type="password"
          placeholder="Enter your new password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={isLoading}
          minLength={6}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="confirmPassword">Confirm New Password</Label>
        <Input
          id="confirmPassword"
          type="password"
          placeholder="Confirm your new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          disabled={isLoading}
          minLength={6}
        />
      </div>

      <Button 
        type="submit" 
        className="w-full" 
        disabled={isLoading || !password.trim() || !confirmPassword.trim()}
      >
        {isLoading ? 'Updating...' : 'Update password'}
      </Button>
    </form>
  );
}
